import { useEffect } from 'react'
import type { IntersectionOptions } from 'react-intersection-observer'
import { useInView } from 'react-intersection-observer'
import { TrackingService } from '../services/TrackingService'
import { ElementViewed } from '../events/ElementViewed'
import type { ElementViewedArgs } from '../events/ElementViewed'

type UseElementViewedConfig = {
  elementViewedArgs: ElementViewedArgs
  opts: IntersectionOptions
  /** When false, the event is not tracked even when the element is in view. Default true. */
  enabled?: boolean
}

/**
 * Hook for tracking when an element is viewed using Intersection Observer
 *
 * Uses react-intersection-observer to detect when a component enters the viewport
 * and triggers a tracking event when the element becomes visible.
 *
 * @param config - Configuration object
 * @param config.elementViewedArgs - Arguments for the ElementViewed tracking event
 * @param config.opts - Intersection Observer options (threshold, rootMargin, etc.)
 * @returns A ref callback to attach to the element you want to track
 *
 * @example
 * ```tsx
 * const ref = useElementViewed({
 *   elementViewedArgs: {
 *     webElement: {
 *       elementType: ElementType.CARD,
 *       location: 'plan-list',
 *       name: 'plan-card',
 *       position: '1',
 *       text: 'Best Value Plan'
 *     }
 *   },
 *   opts: {
 *     threshold: 0.5,
 *     triggerOnce: true
 *   }
 * })
 *
 * return <div ref={ref}>Content to track</div>
 * ```
 */
const useElementViewed = (config: UseElementViewedConfig): ((node?: Element | null) => void) => {
  const { elementViewedArgs, opts, enabled = true } = config
  const [ref, inView] = useInView(opts)

  useEffect(() => {
    if (!inView || !enabled) return
    const elementViewedEvent = new ElementViewed(elementViewedArgs)
    TrackingService.track(elementViewedEvent)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, enabled])

  return ref
}

export default useElementViewed
